<template>
  <div class="app">
    <div v-if="appError" class="app__error" role="alert">
      <span>{{ appError }}</span>
      <button type="button" @click="appError = ''">Закрыть</button>
    </div>
    <div v-if="!ready" class="app__loading">
      <div class="app__loading-logo">
        <img src="/assets/pomesto-logo.svg" alt="поместо" />
        <div class="app__loading-tagline">Делай своё. Строй своё.</div>
      </div>
      <div class="app__loading-spinner"></div>
    </div>
    <template v-else>
    <DesktopAppHeader
      v-if="desktopHeaderVisible"
      :active="stage === 'setup' ? 'profile' : 'rooms'"
      :coins="coins"
      :quest-remaining="questRemaining"
      :unopened-cases="unopenedCases"
      :notification-unread="notificationUnread"
      :admin-role="account?.adminRole || null"
      :avatar-url="account?.avatarUrl || ''"
      @navigate="navigateDesktop"
    />
    <SetupScreen
      v-if="stage === 'setup'"
      :initial="savedInitial"
      :account="account"
      :mode="appearance && userName ? 'profile' : 'setup'"
      :app-navigation="desktopHeaderVisible"
      @done="onSetupDone"
      @open-auth="openAuth"
      @logged-out="onLoggedOut"
    />
    <AdminPanel
      v-else-if="stage === 'admin' && account?.adminRole"
      :role="account.adminRole"
      :account-name="account.displayName || userName || 'Администратор'"
      @close="stage = 'rooms'"
    />
    <RoomsList
      v-else-if="stage === 'rooms'"
      :coins="coins"
      :session-id="sessionId"
      :quest-remaining="questRemaining"
      :unopened-cases="unopenedCases"
      :notification-unread="notificationUnread"
      :admin-role="account?.adminRole || null"
      @pick="onPickRoom"
      @back="openProfile"
      @edit-character="openProfile"
      @open-shop="openShop()"
      @coins-changed="onCoinsChanged"
      @open-tasks="openTasks"
      @open-backpack="openBackpack"
      @open-notifications="openNotifications"
      @open-feedback="openFeedback"
      @open-admin="openAdmin"
    />
    <LibraryScene
      v-else-if="stage === 'scene' && roomId"
      :key="roomId"
      :room-id="roomId"
      :session-id="sessionId"
      :user-name="userName"
      :user-city="userCity"
      :appearance="appearance!"
      :initial-coins="coins"
      :pomodoro="pomodoro"
      :resume-focus="resumeFocus"
      @leave="leaveScene"
      @coins-changed="onCoinsChanged"
      @pomodoro-updated="onPomodoroUpdated"
      @enter-break="onEnterBreak"
      @return-to-focus="onReturnToFocus"
      @open-feedback="openFeedback"
    />
    </template>

    <ShopModal
      v-if="shopOpen && appearance"
      :session-id="sessionId"
      :appearance="appearance"
      :initial-coins="coins"
      :initial-category="shopCategory"
      @close="closeShop"
      @coins-changed="onCoinsChanged"
    />

    <AuthModal
      v-if="authOpen"
      :session-id="sessionId"
      :account="account"
      @close="authOpen = false"
      @authenticated="onAuthenticated"
      @logged-out="onLoggedOut"
    />

    <ProgressHubModal
      v-if="progressOpen"
      :session-id="sessionId"
      :initial-coins="coins"
      :mode="progressMode"
      @close="progressOpen = false"
      @coins-changed="onCoinsChanged"
      @refreshed="onEngagementRefreshed"
    />

    <NotificationCenter
      v-if="notificationOpen"
      :session-id="sessionId"
      @close="notificationOpen = false"
      @unread-changed="onNotificationUnreadChanged"
      @action="onNotificationAction"
    />

    <FeedbackModal
      v-if="feedbackOpen"
      :session-id="sessionId"
      :room-id="roomId || undefined"
      @close="feedbackOpen = false"
      @submitted="onFeedbackSubmitted"
    />

    <button v-if="ready && stage === 'setup' && appearance && userName" class="mobile-feedback-launcher" type="button" aria-label="Открыть обратную связь" title="Обратная связь" @click="openFeedback">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16v12H8l-4 4V5Z"/><path d="M8 9h8m-8 4h5"/></svg>
    </button>

    <DailyStreakPopup
      v-if="dailyPopupVisible && dailyReward"
      :day="dailyReward.day"
      :reward-coins="dailyReward.rewardCoins"
      :case-id="dailyReward.caseId"
      :case-claimed="dailyReward.caseClaimed"
      @shown="markDailySeen"
      @close="dailyReward = null"
      @claim="claimDailyCase"
    />

    <VkFloatingAuth
      v-if="ready && floatingAuthEnabled && !account && !authOpen && !progressOpen && !shopOpen && !notificationOpen && !dailyReward && stage !== 'scene'"
      :session-id="sessionId"
      @authenticated="onAuthenticated"
      @error="onFloatingAuthError"
    />

    <MobileBottomNav
      v-if="mobileNavVisible"
      :active="mobileNavActive"
      :quest-remaining="questRemaining"
      :unopened-cases="unopenedCases"
      @navigate="navigateMobile"
    />

  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, ref } from "vue"
import SetupScreen from "../components/SetupScreen.vue"
import RoomsList from "../components/RoomsList.vue"
import ShopModal from "../components/ShopModal.vue"
import AuthModal from "../components/AuthModal.vue"
import VkFloatingAuth from "../components/VkFloatingAuth.vue"
import ProgressHubModal from "../components/ProgressHubModal.vue"
import NotificationCenter from "../components/NotificationCenter.vue"
import FeedbackModal from "../components/FeedbackModal.vue"
import DailyStreakPopup from "../components/DailyStreakPopup.vue"
import MobileBottomNav, { type MobileSection } from "../components/MobileBottomNav.vue"
import AdminPanel from "../components/AdminPanel.vue"
import DesktopAppHeader, { type DesktopSection } from "../components/DesktopAppHeader.vue"
const LibraryScene = defineAsyncComponent(() => import("../components/LibraryScene.vue"))
import type { PlayerAppearance, PomodoroCycleDto, RoomDto, StudyType } from "../shared/types"
import { roomJoinRoute } from "../api/rooms/join"
import { roomLeaveRoute } from "../api/rooms/leave"
import { getProfileRoute } from "../api/rooms/getProfile"
import { saveProfileRoute } from "../api/rooms/saveProfile"
import { restoreSessionRoute } from "../api/rooms/restoreSession"
import type { RestoredSession } from "../api/rooms/restoreSession"
import type { AuthAccount } from "../api/auth"
import type { EngagementDto } from "../shared/engagementTypes"
import { engagementCheckInRoute } from "../api/engagement/checkIn"
import { attachReferralRoute } from "../api/engagement/attachReferral"
import { markStreakSeenRoute } from "../api/engagement/markStreakSeen"
import { claimCaseRoute } from "../api/engagement/claimCase"
import { engagementStateRoute } from "../api/engagement/state"
import { acquisitionCaptureRoute, type AcquisitionTouch } from "../api/acquisition"
import { METRIKA_GOALS, reachMetrikaGoal } from "../shared/metrika"

type Stage = "setup" | "rooms" | "scene" | "admin"
const stage = ref<Stage>("setup")
const ready = ref(false)

const sessionId = ref<string>("")
const userName = ref("")
const userCity = ref("")
const appearance = ref<PlayerAppearance | null>(null)
const roomId = ref<string | null>(null)
const coins = ref<number>(0)
const studyType = ref<StudyType | null>(null)
const studyValue = ref<string>("")
const appError = ref("")
const shopOpen = ref(false)
const shopCategory = ref<"room" | "hats">("hats")
const authOpen = ref(false)
const floatingAuthEnabled = ref(true)
const account = ref<AuthAccount | null>(null)
const pomodoro = ref<PomodoroCycleDto | null>(null)
const resumeFocus = ref<{ minutes: number; task: string } | null>(null)
const engagement = ref<EngagementDto | null>(null)
const progressOpen = ref(false)
const progressMode = ref<"quests" | "backpack">("quests")
const notificationOpen = ref(false)
const feedbackOpen = ref(false)
const dailyReward = ref<EngagementDto["dailyCheckIn"]>(null)
const questRemaining = computed(() => (engagement.value?.quests.filter((quest) => !quest.completed).length || 0) + (engagement.value?.cases.filter((gift) => !gift.claimed_at && !gift.opened_at).length || 0))
const unopenedCases = computed(() => engagement.value?.cases.filter((gift) => gift.claimed_at && !gift.opened_at).length || 0)
const notificationUnread = computed(() => engagement.value?.notifications.unreadCount || 0)
const desktopHeaderVisible = computed(() => Boolean(appearance.value && userName.value) && (stage.value === "setup" || stage.value === "rooms"))
const mobileNavVisible = computed(() => ready.value && Boolean(appearance.value && userName.value) && stage.value === "rooms" && !authOpen.value)
const mobileNavActive = computed<MobileSection>(() => shopOpen.value ? "shop" : progressOpen.value ? progressMode.value : "rooms")
const dailyPopupVisible = computed(() => ready.value && stage.value === "rooms" && !authOpen.value && !shopOpen.value && !progressOpen.value && !notificationOpen.value)

const savedInitial = ref<
  | { userName?: string; userCity?: string; appearance?: PlayerAppearance; studyType?: StudyType | null; studyValue?: string }
  | undefined
>(undefined)

const STORAGE_KEY = "pomesto.session"
const INVITE_KEY = "pomesto.pendingInvite"
const ATTRIBUTION_KEY = "pomesto.attribution.v1"

function acquisitionTouch(): AcquisitionTouch {
  const url = new URL(window.location.href)
  let referrer = ""
  try {
    const source = document.referrer ? new URL(document.referrer) : null
    if (source && source.hostname !== url.hostname) referrer = source.hostname
  } catch {}
  const clickIdType = url.searchParams.has("gclid") ? "gclid" : url.searchParams.has("yclid") ? "yclid" : url.searchParams.has("vk_click_id") ? "vk_click_id" : ""
  return {
    source: (url.searchParams.get("utm_source") || "").slice(0, 100),
    medium: (url.searchParams.get("utm_medium") || "").slice(0, 100),
    campaign: (url.searchParams.get("utm_campaign") || "").slice(0, 160),
    content: (url.searchParams.get("utm_content") || "").slice(0, 160),
    term: (url.searchParams.get("utm_term") || "").slice(0, 160),
    referrer,
    landingPath: url.pathname.slice(0, 240),
    clickIdType,
  }
}

function hasAcquisitionSignal(touch: AcquisitionTouch) {
  return Boolean(touch.source || touch.medium || touch.campaign || touch.content || touch.term || touch.referrer || touch.clickIdType)
}

function attributionPayload() {
  const current = acquisitionTouch()
  let saved: { first?: AcquisitionTouch; last?: AcquisitionTouch } = {}
  try { saved = JSON.parse(localStorage.getItem(ATTRIBUTION_KEY) || "{}") } catch {}
  const first = saved.first || current
  const last = hasAcquisitionSignal(current) ? current : (saved.last || current)
  try { localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify({ first, last })) } catch {}
  return { first, last }
}

function rememberInvite() {
  try {
    const url = new URL(window.location.href)
    const code = (url.searchParams.get("invite") || "").trim().toUpperCase()
    if (!code) return
    localStorage.setItem(INVITE_KEY, code)
    url.searchParams.delete("invite")
    history.replaceState({}, "", url.pathname + url.search + url.hash)
  } catch {}
}

async function syncEngagement() {
  if (!appearance.value || !sessionId.value) return
  try {
    const state = await engagementCheckInRoute.run(ctx, { sessionId: sessionId.value })
    engagement.value = state
    if (state.dailyCheckIn) dailyReward.value = state.dailyCheckIn
    coins.value = state.coins
    const inviteCode = localStorage.getItem(INVITE_KEY)
    if (inviteCode) {
      await attachReferralRoute.run(ctx, { sessionId: sessionId.value, inviteCode })
      localStorage.removeItem(INVITE_KEY)
    }
  } catch {}
}

function applyRestoredSession(res: RestoredSession) {
  if (typeof res.coins === "number") coins.value = res.coins
  account.value = res.account || null
  pomodoro.value = res.pomodoro || null
  if (!res.profile) return
  const p = res.profile
  const profileAppearance: PlayerAppearance = {
    gender: p.gender === "male" ? "male" : "female",
    characterKey: p.characterKey || "",
    hairKey: p.hairKey || "",
    clothesKey: p.clothesKey || "",
    skinColor: p.skinColor || "",
    hairColor: p.hairColor || "",
    clothesColor: p.clothesColor || "",
  }
  savedInitial.value = {
    userName: p.userName || "",
    userCity: p.userCity || "",
    appearance: profileAppearance,
    studyType: p.studyType || null,
    studyValue: p.studyValue || "",
  }
  userName.value = p.userName || ""
  userCity.value = p.userCity || ""
  appearance.value = profileAppearance
  studyType.value = p.studyType || null
  studyValue.value = p.studyValue || ""
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(savedInitial.value))
  } catch (e) {}
}

onMounted(async () => {
  rememberInvite()
  ready.value = false
  let existing = ""
  try {
    existing = localStorage.getItem(STORAGE_KEY + ".id") || ""
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      savedInitial.value = {
          userName: parsed.userName,
          userCity: parsed.userCity,
          appearance: parsed.appearance,
          studyType: parsed.studyType || null,
          studyValue: parsed.studyValue || "",
        }
      if (parsed.userName) userName.value = parsed.userName
      if (parsed.userCity) userCity.value = parsed.userCity
      if (parsed.appearance) appearance.value = parsed.appearance
      if (parsed.studyType) studyType.value = parsed.studyType
      if (parsed.studyValue) studyValue.value = parsed.studyValue
    }
  } catch (e) {}

  if (!existing) {
    existing =
      "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36)
    try {
      localStorage.setItem(STORAGE_KEY + ".id", existing)
    } catch (e) {}
  }
  sessionId.value = existing

  try {
    await acquisitionCaptureRoute.run(ctx, { sessionId: existing, ...attributionPayload() })
  } catch {}

  try {
      const res = await restoreSessionRoute.query({ sessionId: existing }).run(ctx)
      applyRestoredSession(res)
      await syncEngagement()
      if (res.activeRoomId && appearance.value) {
        roomId.value = res.activeRoomId
        stage.value = "scene"
      } else if (appearance.value && userName.value) {
        stage.value = "rooms"
      }
    } catch (e) {
      if (appearance.value && userName.value) stage.value = "rooms"
      appError.value = "Не удалось синхронизировать профиль. Локальные настройки сохранены."
    }
  ready.value = true
})

async function onAuthenticated(value: AuthAccount, isNewAccount = false) {
  floatingAuthEnabled.value = false
  account.value = value
  if (isNewAccount) reachMetrikaGoal(METRIKA_GOALS.registration, { provider: value.provider })
  try {
    const restored = await restoreSessionRoute.query({ sessionId: sessionId.value }).run(ctx)
    applyRestoredSession(restored)
    await syncEngagement()
    if (restored.activeRoomId && appearance.value) {
      roomId.value = restored.activeRoomId
      stage.value = "scene"
    } else if (appearance.value && userName.value) {
      stage.value = "rooms"
    }
    authOpen.value = false
  } catch {
    appError.value = "Вход выполнен, но профиль пока не удалось обновить. Попробуй перезагрузить страницу."
  }
}

function onLoggedOut() {
  account.value = null
  authOpen.value = false
  floatingAuthEnabled.value = false
}

function openAuth() {
  // Opening the full sign-in dialog is an explicit choice. Do not show the
  // floating prompt again when that dialog closes during the same page visit.
  floatingAuthEnabled.value = false
  authOpen.value = true
}

function onFloatingAuthError(message: string) {
  // The floating prompt is optional; keep the guest experience uninterrupted.
  console.info(message)
}

function onCoinsChanged(value: number) {
  coins.value = value
}

function openTasks() {
  notificationOpen.value = false
  progressMode.value = "quests"
  progressOpen.value = true
}

function openBackpack() {
  notificationOpen.value = false
  progressMode.value = "backpack"
  progressOpen.value = true
}

function onEngagementRefreshed(value: EngagementDto) {
  engagement.value = value
}

function onNotificationUnreadChanged(value: number) {
  if (engagement.value) engagement.value.notifications.unreadCount = value
}

function openNotifications() {
  shopOpen.value = false
  progressOpen.value = false
  notificationOpen.value = true
}

function openFeedback() {
  shopOpen.value = false
  progressOpen.value = false
  notificationOpen.value = false
  feedbackOpen.value = true
}

async function onFeedbackSubmitted() { await syncEngagement() }

function openAdmin() {
  if (!account.value?.adminRole) return
  shopOpen.value = false
  progressOpen.value = false
  notificationOpen.value = false
  dailyReward.value = null
  stage.value = "admin"
}

function openProfile() {
  shopOpen.value = false
  progressOpen.value = false
  notificationOpen.value = false
  dailyReward.value = null
  stage.value = "setup"
  window.scrollTo({ top: 0, behavior: "smooth" })
}

async function openPersonalRoom() {
  await onPickRoom({
    id: "personal",
    title: "Моя комната",
    description: "Личное пространство для фокуса и своих вещей",
    capacity: 1,
    occupied: 0,
    humanOccupied: 0,
    residentOccupied: 0,
    roomType: "PERSONAL",
  })
}

async function navigateDesktop(section: DesktopSection) {
  if (section === "profile") return openProfile()
  if (section === "personal") return openPersonalRoom()
  if (section === "quests") return openTasks()
  if (section === "backpack") return openBackpack()
  if (section === "shop") return openShop()
  if (section === "notifications") return openNotifications()
  if (section === "feedback") return openFeedback()
  if (section === "admin") return openAdmin()
  shopOpen.value = false
  progressOpen.value = false
  notificationOpen.value = false
  dailyReward.value = null
  stage.value = "rooms"
  window.scrollTo({ top: 0, behavior: "smooth" })
}

function onNotificationAction(type: string) {
  notificationOpen.value = false
  if (type === "case") openTasks()
  else if (type === "backpack") openBackpack()
}

async function markDailySeen() {
  try { await markStreakSeenRoute.run(ctx, { sessionId: sessionId.value }) } catch {}
}

async function claimDailyCase() {
  const reward = dailyReward.value
  if (!reward?.caseId) return
  try {
    if (!reward.caseClaimed) await claimCaseRoute.run(ctx, { sessionId: sessionId.value, caseId: reward.caseId })
    engagement.value = await engagementStateRoute.query({ sessionId: sessionId.value }).run(ctx)
    coins.value = engagement.value.coins
    dailyReward.value = null
    openBackpack()
  } catch { appError.value = "Не удалось получить сундук. Он останется в заданиях — попробуй ещё раз." }
}

function openShop(category: "room" | "hats" = "hats") {
  shopCategory.value = category
  shopOpen.value = true
}

async function closeShop() {
  shopOpen.value = false
  await syncEngagement()
}

async function navigateMobile(section: MobileSection) {
  notificationOpen.value = false
  if (section === "quests" || section === "backpack") {
    shopOpen.value = false
    progressMode.value = section
    progressOpen.value = true
    return
  }
  if (section === "shop") {
    progressOpen.value = false
    openShop()
    return
  }
  shopOpen.value = false
  progressOpen.value = false
  if (section === "rooms") return
  await openPersonalRoom()
}

function onPomodoroUpdated(value: PomodoroCycleDto) {
  pomodoro.value = value
}

function onEnterBreak(value: PomodoroCycleDto) {
  pomodoro.value = value
  resumeFocus.value = null
  roomId.value = "social"
}

async function onReturnToFocus(payload: { roomId: string; minutes: number; task: string; pomodoro: PomodoroCycleDto }) {
  pomodoro.value = payload.pomodoro
  resumeFocus.value = { minutes: payload.minutes, task: payload.task }
  await onPickRoom({
    id: payload.roomId,
    title: "",
    description: "",
    capacity: 0,
    occupied: 0,
    humanOccupied: 0,
    residentOccupied: 0,
    roomType: "LIBRARY",
  })
}

async function onSetupDone(payload: {
  userName: string
  userCity: string
  appearance: PlayerAppearance
  studyType: StudyType | null
  studyValue: string
}) {
  const isFirstCompletion = !savedInitial.value?.userName || !savedInitial.value?.appearance
  userName.value = payload.userName
  userCity.value = payload.userCity
  appearance.value = payload.appearance
  studyType.value = payload.studyType
  studyValue.value = payload.studyValue
  savedInitial.value = { userName: payload.userName, userCity: payload.userCity, appearance: payload.appearance, studyType: payload.studyType, studyValue: payload.studyValue }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch (e) {}
  try {
    await saveProfileRoute.run(ctx, { sessionId: sessionId.value, ...payload, ...payload.appearance })
    if (isFirstCompletion) {
      reachMetrikaGoal(METRIKA_GOALS.characterCompleted, { character_key: payload.appearance.characterKey })
    }
    await syncEngagement()
  } catch (e) {
    appError.value = "Профиль сохранён на устройстве, но пока не синхронизирован с сервером."
  }
  stage.value = "rooms"
}

async function onPickRoom(room: RoomDto) {
  if (!appearance.value) return
  appError.value = ""
  try {
    const res = await roomJoinRoute.run(ctx, {
    roomId: room.id,
    sessionId: sessionId.value,
    userName: userName.value,
    userCity: userCity.value,
    gender: appearance.value.gender,
    characterKey: appearance.value.characterKey,
    hairKey: appearance.value.hairKey,
    clothesKey: appearance.value.clothesKey,
    skinColor: appearance.value.skinColor,
    hairColor: appearance.value.hairColor,
    clothesColor: appearance.value.clothesColor,
    studyType: studyType.value || undefined,
    studyValue: studyValue.value || undefined,
  })
    if (!res.success) {
      appError.value = ("error" in res && res.error) || "Не удалось присоединиться"
      return
    }
    roomId.value = res.roomId ?? null
    stage.value = "scene"
    reachMetrikaGoal(METRIKA_GOALS.roomEntered, { room_id: room.id, room_type: room.roomType })
    if (room.id === "personal") reachMetrikaGoal(METRIKA_GOALS.personalRoomEntered)
  } catch (e) {
    appError.value = "Не удалось войти в комнату. Проверь соединение и попробуй ещё раз."
  }
}

async function leaveScene() {
  try {
    await roomLeaveRoute.run(ctx, { sessionId: sessionId.value })
  } catch (e) {}
  roomId.value = null
  try {
      const res = await getProfileRoute.query({ sessionId: sessionId.value }).run(ctx)
      if (typeof res.coins === "number") coins.value = res.coins
    } catch (e) {}
  await syncEngagement()
  stage.value = "rooms"
}
</script>

<style scoped>
.app {
  min-height: 100vh;
}
.app__error {
  position: fixed;
  z-index: 10000;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  width: min(560px, calc(100vw - 24px));
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  border-radius: 16px;
  color: #292B33;
  background: #FFF3D7;
  border: 1px solid rgba(242, 184, 75, .55);
  box-shadow: var(--pomesto-shadow);
  font-size: 14px;
  font-weight: 600;
}
.app__error button { min-width: 44px; min-height: 44px; border: 0; border-radius: 12px; background: #6258D9; color: white; font-weight: 700; }
.app__loading {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 32px;
  background: #F6F2E9;
}
.app__loading-logo {
  text-align: center;
}
.app__loading-logo img {
  width: min(220px, 62vw);
  display: block;
}
.app__loading-tagline {
  margin-top: 16px;
  font-size: 14px;
  font-weight: 500;
  color: #666873;
}
.app__loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid rgba(98, 88, 217, 0.18);
  border-top-color: #6258D9;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
.mobile-feedback-launcher{display:none}
@media(max-width:767px){.mobile-feedback-launcher{position:fixed;z-index:6500;top:max(12px,env(safe-area-inset-top));right:12px;display:grid;width:42px;height:42px;place-items:center;border:1px solid rgba(41,43,51,.1);border-radius:14px;background:#fffdf8;color:#6258d9;box-shadow:0 6px 18px rgba(41,43,51,.13)}.mobile-feedback-launcher svg{width:22px;height:22px;fill:none;stroke:currentColor;stroke-width:1.9;stroke-linecap:round;stroke-linejoin:round}}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
